import {
  Stack,
  Button,
  Typography,
  Collapse,
  List,
  ListItem,
  Paper,
  Box,
  alpha,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Chip,
} from "@mui/material";
import { Node } from "../types/assistantTypes";
import React from "react";
import AlgorithmBulletPoints from "./AlgorithmBulletPoints";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

interface Props {
  question: Node;
  onAnswer: (answer: number) => void;
  algorithmId?: string;
}

const LATEST_ALGORITHM_ID = "Tdz7AhAe5FMN96TZRWH3";
const REFERENCE_TABLE_URL = "https://docs.google.com/spreadsheets/d/e/2PACX-1vSV38loqDF8bFwnCFkHveJjuawD6zlaAR9wbDzXitsnqG-IP7fWmwE2EG51qCIXDg/pubhtml?gid=1581462884&single=true&widget=true&headers=false";

const Question: React.FC<Props> = (props) => {
  const [selectedAnswer, setSelectedAnswer] = React.useState<number | null>(null);
  const [showDefinitions, setShowDefinitions] = React.useState(false);
  const [showReferenceTable, setShowReferenceTable] = React.useState(false);

  const shouldShowReferenceTable = props.algorithmId === LATEST_ALGORITHM_ID && props.question.id === 0;

  const handleAnswerSelect = (answerId: number) => {
    setSelectedAnswer(answerId);
  };

  const handleSubmit = () => {
    if (selectedAnswer !== null) {
      props.onAnswer(selectedAnswer);
      setSelectedAnswer(null);
      setShowDefinitions(false);
    }
  };

  return (
    <React.Fragment>
      <Dialog
        open={showReferenceTable}
        onClose={() => setShowReferenceTable(false)}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            backgroundImage: (theme) => `linear-gradient(180deg, ${alpha(theme.palette.secondary.light, 0.18)}, ${theme.palette.background.paper})`,
          }
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, sm: 3 },
            pt: { xs: 2, sm: 3 },
            pb: 1,
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Chip label="Referencia 2026" color="secondary" size="small" />
            <Typography variant="h6" sx={{ fontWeight: 700, pr: 5 }}>
              Guia de dosificacion de antimicrobianos
            </Typography>
          </Stack>
          <IconButton
            onClick={() => setShowReferenceTable(false)}
            sx={{ position: 'absolute', top: 14, right: 14 }}
          >
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 2 }, pb: { xs: 1.5, sm: 2.5 } }}>
          <Box
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              boxShadow: (theme) => `0 20px 45px ${alpha(theme.palette.common.black, 0.12)}`,
            }}
          >
            <Box
              component="iframe"
              src={REFERENCE_TABLE_URL}
              title="Tabla de referencia del algoritmo 2026"
              sx={{
                width: '100%',
                minHeight: { xs: '60vh', md: '72vh' },
                border: 0,
                display: 'block',
                bgcolor: 'common.white',
              }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <Stack
        spacing={4}
        sx={{
          maxWidth: 800,
          mx: 'auto',
          width: '100%',
          py: 4,
          px: { xs: 2, sm: 4 }
        }}
      >
        <Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              textAlign: 'center',
              mb: 3
            }}
          >
            {props.question.question}
          </Typography>

          {shouldShowReferenceTable && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 3,
                backgroundImage: (theme) => `linear-gradient(135deg, ${alpha(theme.palette.secondary.light, 0.22)}, ${alpha(theme.palette.primary.light, 0.08)})`,
              }}
            >
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 0.5 }}>
                    Guia de dosificacion de antimicrobianos
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Antes de responder el primer paso, podes abrir esta guia para revisar la tabla de dosificacion de antimicrobianos.
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => setShowReferenceTable(true)}
                  sx={{ minWidth: 190, fontWeight: 700 }}
                >
                  Abrir tabla
                </Button>
              </Stack>
            </Paper>
          )}

          {props.question.bulletPoints && (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6)
              }}
            >
              <AlgorithmBulletPoints bulletPoints={props.question.bulletPoints} />
            </Paper>
          )}

          {props.question.description && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <Button
                onClick={() => setShowDefinitions(!showDefinitions)}
                color={showDefinitions ? 'primary' : 'inherit'}
                startIcon={<InfoOutlinedIcon />}
                sx={{
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  border: 1,
                  borderColor: showDefinitions ? 'primary.main' : 'divider',
                  bgcolor: (theme) => showDefinitions ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                  color: (theme) => showDefinitions ? theme.palette.primary.main : theme.palette.text.primary,
                  '&:hover': {
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    borderColor: (theme) => showDefinitions ? theme.palette.primary.main : theme.palette.primary.light,
                  }
                }}
              >
                Definiciones
              </Button>
            </Box>
          )}

          <Collapse in={showDefinitions}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: (theme) => alpha(theme.palette.background.paper, 0.6)
              }}
            >
              <List>
                {props.question.description?.split("*(").map((paragraph, idx) => (
                  <ListItem key={`paragraph-${idx}`}>
                    <Typography variant="body2" color="text.secondary">
                      {paragraph}
                    </Typography>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Collapse>
        </Box>

        <Stack spacing={2}>
          {props.question.answers.map((answer) => (
            <Button
              key={answer.toNode}
              variant={selectedAnswer === answer.toNode ? "contained" : "outlined"}
              onClick={() => handleAnswerSelect(answer.toNode)}
              sx={{
                py: 2,
                px: 3,
                justifyContent: 'flex-start',
                textAlign: 'left',
                borderWidth: 2,
                position: 'relative',
                '&:hover': {
                  borderWidth: 2,
                },
                ...(selectedAnswer === answer.toNode && {
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: -3,
                    border: (theme) => `3px solid ${theme.palette.primary.main}`,
                    borderRadius: 'inherit',
                    opacity: 0.5,
                  }
                })
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <Typography
                  variant="body1"
                  sx={{
                    flex: 1,
                    fontWeight: selectedAnswer === answer.toNode ? 600 : 400
                  }}
                >
                  {answer.answer}
                </Typography>
                {selectedAnswer === answer.toNode && (
                  <CheckCircleIcon sx={{ ml: 1 }} />
                )}
              </Box>
            </Button>
          ))}
        </Stack>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            size="large"
            disabled={selectedAnswer === null}
            onClick={handleSubmit}
            sx={{
              minWidth: 200,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 600,
              borderRadius: 3,
              bgcolor: (theme: any) => selectedAnswer === null ? alpha(theme.palette.action.disabled, 0.12) : theme.palette.primary.main,
              color: (theme: any) => selectedAnswer === null ? theme.palette.action.disabled : theme.palette.primary.contrastText,
              opacity: selectedAnswer === null ? 0.7 : 1,
              '&:hover': {
                bgcolor: (theme: any) => selectedAnswer === null ? alpha(theme.palette.action.disabled, 0.12) : theme.palette.primary.dark,
              }
            }}
          >
            Siguiente
          </Button>
        </Box>
      </Stack>
    </React.Fragment>
  );
};

export default Question;
